import { AlertDangerIcon, Feedback, PlusIcon, RenderValueProps, SelectInput } from 'canoe';
import { Field, FieldAttributes, useFormikContext } from 'formik';
import get from 'lodash/get';
import React from 'react';
import { StyledComponent } from 'styled-components';

import { RenderItemProps } from 'canoe/components/List';

import { DefaultItemProps } from './Select';

export interface SelectFieldProps<Item = DefaultItemProps> {
  name: string;
  readOnly?: boolean;
  items: Item[];
  placeholder?: string;
  emptyPlaceholder?: string;
  onSelect: (item: Item, validate?: boolean) => void;
  onFilter?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  searchValue?: string;
  onClearSearch?: () => void;
  renderItem?: (itemProps: RenderItemProps<Item>) => React.ReactNode;
  renderValue?: (valueProps: RenderValueProps<Item>) => React.ReactNode;
  loading?: boolean;
  selectedItem?: Item | Item[];
  noAvailableOptions: boolean;
  isMultiselect?: boolean;
  InputWrapper?: StyledComponent<'div', any, { isMultiselect?: boolean | undefined }>;
  'data-cy'?: string;
  errorMessage?: string;
  validateOnSelectWithErrors?: boolean;
  handleEmptyValue?: { showEmptyValue: boolean; warningText: string };
}

interface InputProps<Item = DefaultItemProps> extends Omit<SelectFieldProps<Item>, 'InputWrapper'> {
  fieldAttributes: FieldAttributes<any>;
}

const Input = <Item extends DefaultItemProps>({
  name,
  noAvailableOptions,
  items,
  selectedItem,
  isMultiselect,
  renderItem,
  renderValue,
  readOnly,
  placeholder,
  emptyPlaceholder = 'No options available',
  loading,
  onSelect,
  onFilter,
  onClearSearch,
  searchValue,
  errorMessage,
  'data-cy': dataCy = 'autocomplete-selector',
  fieldAttributes,
  validateOnSelectWithErrors,
  handleEmptyValue,
}: InputProps<Item>) => {
  const { field, meta } = fieldAttributes;
  const { values } = useFormikContext();
  const { showEmptyValue, warningText } = handleEmptyValue ?? {};

  return (
    <>
      <SelectInput<Item>
        {...field}
        data-cy={dataCy}
        className={dataCy}
        id={`${dataCy}-${name}`}
        disabled={noAvailableOptions || readOnly}
        placeholder={noAvailableOptions ? emptyPlaceholder : placeholder}
        state={
          (meta.touched && meta.error) || errorMessage || noAvailableOptions || showEmptyValue
            ? `${showEmptyValue ? 'warning' : 'danger'}`
            : 'default'
        }
        selectedItem={
          showEmptyValue && !selectedItem ? { label: get(values, name), value: '' } : selectedItem
        }
        onSelect={(selected: Item) => {
          onSelect(
            selected,
            validateOnSelectWithErrors && ((meta.touched && meta.error) || errorMessage),
          );
        }}
        items={items}
        searchable={!!onFilter}
        onFilter={onFilter}
        searchInputProps={
          onFilter
            ? {
                placeholder:
                  placeholder || (isMultiselect ? 'Search or paste multiple' : 'Type to filter'),
                // control the searchValue explicitly
                value: searchValue || '',
              }
            : undefined
        }
        onClearFilter={onClearSearch}
        onOpeningChange={(isOpen) => {
          if (!isOpen) {
            onClearSearch?.();
          }
        }}
        renderItem={renderItem}
        renderValue={renderValue}
        clearable={false}
        loading={loading}
        placeholderIcon={
          noAvailableOptions ? (
            <AlertDangerIcon color="OnyxGrayD10" />
          ) : (
            <PlusIcon color="OnyxGrayD10" />
          )
        }
      />
      {((meta.touched && meta.error) || errorMessage || showEmptyValue) && (
        <Feedback mt="xxs" color={showEmptyValue ? 'warning' : 'danger'}>
          {showEmptyValue ? warningText : `${meta.error || errorMessage}`}
        </Feedback>
      )}
    </>
  );
};

export const SelectField = <Item extends DefaultItemProps>({
  name,
  isMultiselect,
  InputWrapper,
  ...props
}: SelectFieldProps<Item>) => {
  return (
    <Field name={name}>
      {(attrs: FieldAttributes<any>) =>
        InputWrapper ? (
          <InputWrapper isMultiselect={isMultiselect}>
            <Input<Item>
              name={name}
              isMultiselect={isMultiselect}
              fieldAttributes={attrs}
              {...props}
            />
          </InputWrapper>
        ) : (
          <Input<Item>
            name={name}
            isMultiselect={isMultiselect}
            fieldAttributes={attrs}
            {...props}
          />
        )
      }
    </Field>
  );
};
